import { AnalyticsService } from './analytics.service';
import { TravelAdvisorProfileServiceFactory } from './travel-advisor-profile.service';
import { UserService } from "./user.service";
// https://angularfirebase.com/snippets/angularfire2-version-4-authentication-service/

import { Injectable } from "@angular/core";

import { AngularFireAuth } from "@angular/fire/auth";
import * as firebase from "firebase";

import { User } from "./user.model";

import { Observable, of } from "rxjs";
import { switchMap } from "rxjs/operators";

@Injectable()
export class AuthService {
  user$: Observable<User>;
  user: User;

  constructor(
    private afAuth: AngularFireAuth,
    private userService: UserService,
    private profileServiceFactory: TravelAdvisorProfileServiceFactory,
    private analyticsService: AnalyticsService
  ) {
    this.user$ = this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          return this.userService.get(user.uid);
        } else {
          return of(null);
        }
      })
    );

    this.user$.subscribe((user: User) => {
      this.user = user;
      (<any>window).stackdriverErrorReporter.setUser(user ? user.id : null);
    });
  }

  signInWithGoogle() {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    return this.signInWithProvider(provider);
  }

  signInWithFacebook() {
    const provider = new firebase.auth.FacebookAuthProvider();
    return this.signInWithProvider(provider);
  }

  private signInWithProvider(provider) {
    return this.afAuth
      .signInWithPopup(provider)
      .then(credential => {
        this.analyticsService.track("login", { method: (provider as firebase.auth.AuthProvider).providerId });
        return this.handleLoggedInUser(credential.user);
      })
      .catch(error => {
        if (error.code == "auth/account-exists-with-different-credential") {
          this.afAuth
            .fetchSignInMethodsForEmail(error.email)
            .then(providers => {
              alert(
                "Per proseguire, devi prima autenticarti prima con il tuo account " +
                providers[0]
              );

              // FIXME: il popup a sto punto viene bloccato...

              let mainProvider;

              switch (providers[0]) {
                case "google.com":
                  mainProvider = new firebase.auth.GoogleAuthProvider();
                  provider.setCustomParameters({ login_hint: error.email });
                  break;

                case "facebook.com":
                  mainProvider = new firebase.auth.FacebookAuthProvider();
                  break;
              }

              this.afAuth
                .signInWithPopup(mainProvider)
                .then(credential => {
                  credential.user.linkWithCredential(error.credential);
                  return this.handleLoggedInUser(credential.user);
                })
                .catch(error2 => { throw error2; });
            })
            .catch(error2 => { throw error2; });
        } else { throw error; }
      });
  }

  signInWithEmailAndPassword(email: string, password: string) {
    return this.afAuth
      .signInWithEmailAndPassword(email, password)
      .then(credential => {
        return this.handleLoggedInUser(credential.user);
      })
      .catch(error => { throw error; });
  }

  createUserWithEmailAndPassword(email: string, password: string) {
    return this.afAuth
      .createUserWithEmailAndPassword(email, password)
      .then(credential => {
        return this.handleLoggedInUser(credential.user);
      })
      .catch(error => { throw error; });
  }

  signOut() {
    this.analyticsService.track("logout");
    return this.afAuth.signOut();
  }

  private handleLoggedInUser(userData: firebase.User) {
    this.userService.exists(userData.uid).subscribe(async exists => {
      if (!exists) {
        await this.createUserData(userData.uid);
        await this.createUserProfile(userData.uid);
      }
      this.updateUserData(userData);
    });
  }

  private updateUserData(userData: firebase.User) {
    const user: Partial<User> = {
      id: userData.uid,
      email: userData.email,
      displayName: userData.displayName,
      photoUrl: userData.photoURL
    };
    return this.userService.update(user);
  }

  private createUserData(userId: string) {
    const user: Partial<User> = {
      id: userId,
      roles: []
    };
    return this.userService.create(user);
  }

  private createUserProfile(userId: string) {
    const profile = {
      id: "default"
    };
    return this.profileServiceFactory.create(userId).create(profile);
  }
}
